import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { chatAPI } from '../../chat/api/chatAPI';
import { FormattedChatMessage } from '../../chat/utils/chatMessageFormat';
import {
  shouldReuseEmptyDraft,
  shouldDiscardEmptyDraftOnSelect,
  shouldHideSessionFromSidebar,
  orphanEmptyDraftIds,
} from '../../chat/utils/emptyChatDraft';
import { useAuth } from '../../auth/hooks/useAuth';
import { onboardingAPI } from '../../onboarding/api/onboardingAPI';
import OnboardingOptionCard from '../../onboarding/components/OnboardingOptionCard';
import OnboardingSummaryCard from '../../onboarding/components/OnboardingSummaryCard';
import OnboardingDivider from '../../onboarding/components/OnboardingDivider';
import OnboardingInvitationCard from '../../onboarding/components/OnboardingInvitationCard';
import { isPathbuddyHost } from '../../../config/eduHost';
import { isInstructorAccount } from '../../../utils/isInstructorAccount';
import usePathsChat from '../../paths/hooks/usePathsChat';
import PathsChatSurface, { PathsThread, PathsComposerAccessory } from '../../paths/components/PathsChatSurface';
import MyPathFolder from '../../paths/components/MyPathFolder';
import './ChatAgentPanel.css';

// Instructor empty state only. Clicking fills the composer; it never sends.
export const INSTRUCTOR_STARTER_PROMPTS = [
  'Write a mark scheme for an LNAT essay question',
  'Create 10 UCAT verbal reasoning questions with worked answers',
  'Help me draft a UCAS reference for a law applicant',
  'Draft an email to parents about mock interviews',
  'Which Pathbuddy apps suit Oxbridge interview prep?',
];

// Mirrors onboarding_steps.py's is_step_active/active_steps exactly — a
// step is only active if the named prior step's saved answer equals the
// given value. Kept in sync manually with the backend (both are small,
// declarative, and reviewed together); not fetched over the network
// separately since it's pure branching logic over data already fetched.
function isOnboardingStepActive(step, answers) {
  if (!step.when) return true;
  return answers[step.when.step] === step.when.equals;
}

function activeOnboardingSteps(steps, answers) {
  return steps.filter((s) => isOnboardingStepActive(s, answers));
}

// Must match ONBOARDING_SUMMARY_ACK in onboarding_service.py exactly — the
// fixed acknowledgement value the 'summary' step's answer always is.
const ONBOARDING_SUMMARY_ACK = 'acknowledged';

function formatPlanLabel(activePlan) {
  if (!activePlan || typeof activePlan !== 'string') return 'Free';
  return activePlan
    .split(/[_\s]+/)
    .filter(Boolean)
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
    .join(' ');
}

function getFirstName(user) {
  if (user?.first_name?.trim()) return user.first_name.trim();
  const name = (user?.name || '').trim();
  if (!name) return 'there';
  return name.split(/\s+/)[0];
}

const THINKING_STATUSES = [
  'Thinking…',
  'Analysing…',
  'Formulating…',
  'Writing…',
];

// Fast typewriter: ~400ms max; skip entirely for short replies.
const TYPEWRITER_TARGET_MS = 400;
const TYPEWRITER_TICK_MS = 8;
const TYPEWRITER_SKIP_UNDER_CHARS = 80;

function ChatSendSlot({ input, loading, sessionId, onSend, locked }) {
  const hasText = (input || '').trim().length > 0;

  return (
    <div className="chat-agent-send-slot">
      <button
        type="button"
        className="chat-agent-send-green"
        onClick={onSend}
        disabled={loading || !sessionId || !hasText || locked}
        aria-label="Send"
      >
        <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.8" strokeLinecap="round" strokeLinejoin="round">
          <line x1="12" y1="19" x2="12" y2="5" />
          <polyline points="5 12 12 5 19 12" />
        </svg>
      </button>
    </div>
  );
}

function ThinkingIndicator() {
  const [statusIndex, setStatusIndex] = useState(0);
  const [fading, setFading] = useState(false);

  useEffect(() => {
    let fadeTimer;
    const id = window.setInterval(() => {
      setFading(true);
      fadeTimer = window.setTimeout(() => {
        setStatusIndex((i) => (i + 1) % THINKING_STATUSES.length);
        setFading(false);
      }, 180);
    }, 1100);
    return () => {
      window.clearInterval(id);
      window.clearTimeout(fadeTimer);
    };
  }, []);

  return (
    <div className="chat-agent-thinking" role="status" aria-live="polite" aria-label="Assistant is thinking">
      <div className="chat-agent-thinking-pulse" aria-hidden="true">
        <span className="chat-agent-thinking-pulse-ring" />
        <span className="chat-agent-thinking-pulse-core" />
      </div>
      <span className={`chat-agent-thinking-label${fading ? ' fading' : ''}`}>
        {THINKING_STATUSES[statusIndex]}
      </span>
    </div>
  );
}

function StreamPauseIndicator() {
  return (
    <span className="chat-agent-stream-pause" aria-hidden="true">
      <span className="chat-agent-stream-pause-label">Thinking…</span>
      <span className="chat-agent-stream-pause-dot" />
      <span className="chat-agent-stream-pause-dot" />
      <span className="chat-agent-stream-pause-dot" />
    </span>
  );
}

function BotMessageContent({ text, animate, partial, showPauseIndicator, onComplete, onProgress }) {
  const [displayed, setDisplayed] = useState(() => (animate ? '' : (text || '')));

  useEffect(() => {
    if (!animate) {
      return undefined;
    }

    const full = text || '';
    if (!full) {
      onComplete?.();
      return undefined;
    }

    // Short replies feel instant — no extra animation delay.
    if (full.length < TYPEWRITER_SKIP_UNDER_CHARS) {
      setDisplayed(full);
      onComplete?.();
      return undefined;
    }

    setDisplayed('');
    const totalTicks = Math.max(1, Math.ceil(TYPEWRITER_TARGET_MS / TYPEWRITER_TICK_MS));
    const charsPerStep = Math.max(1, Math.ceil(full.length / totalTicks));
    let index = 0;
    let timerId;

    const tick = () => {
      index = Math.min(full.length, index + charsPerStep);
      const next = full.slice(0, index);
      setDisplayed(next);
      onProgress?.();
      if (index >= full.length) {
        onComplete?.();
      } else {
        timerId = window.setTimeout(tick, TYPEWRITER_TICK_MS);
      }
    };

    timerId = window.setTimeout(tick, TYPEWRITER_TICK_MS);
    return () => window.clearTimeout(timerId);
  }, [text, animate, onComplete, onProgress]);

  const visibleText = animate ? displayed : (text || '');
  const showCursor = (animate && visibleText.length < (text || '').length) || (partial && !showPauseIndicator);

  return (
    <div className={`chat-agent-msg-formatted-wrap${animate || partial ? ' typing' : ''}`}>
      <FormattedChatMessage text={visibleText} partial={animate || partial} />
      {showPauseIndicator && <StreamPauseIndicator />}
      {showCursor && <span className="chat-agent-typing-cursor" aria-hidden="true" />}
    </div>
  );
}

function getTimeBasedGreeting(user) {
  const first = getFirstName(user);
  const hour = new Date().getHours();

  if (hour >= 5 && hour < 12) {
    return `Hi, ${first}, how can I help you this morning?`;
  }
  if (hour >= 12 && hour < 14) {
    return `Hey, ${first}, what can I do for you?`;
  }
  if (hour >= 14 && hour < 17) {
    return `Good afternoon, ${first}. What's the priority?`;
  }
  if (hour >= 17 && hour < 22) {
    return `Good evening, ${first}. What can I do for you?`;
  }
  if (hour >= 22 || hour < 2) {
    return `${first} — one clear ask. We'll build from there.`;
  }
  return `Early start, ${first}. What are we solving first?`;
}



function formatSessionDate(dateStr) {
  if (!dateStr) return '';
  const d = new Date(dateStr);
  if (Number.isNaN(d.getTime())) return '';
  const now = new Date();
  const sameYear = d.getFullYear() === now.getFullYear();
  return d.toLocaleDateString(undefined, sameYear
    ? { month: 'short', day: 'numeric' }
    : { month: 'short', day: 'numeric', year: 'numeric' });
}

function CreateProjectModal({ open, name, onNameChange, onClose, onCreate, creating }) {
  if (!open) return null;
  const trimmed = (name || '').trim();
  return (
    <div className="chat-agent-modal-backdrop" onClick={onClose} role="presentation">
      <div
        className="chat-agent-modal"
        role="dialog"
        aria-labelledby="create-project-title"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="chat-agent-modal-header">
          <h2 id="create-project-title" className="chat-agent-modal-title">Create project</h2>
          <button type="button" className="chat-agent-modal-close" onClick={onClose} aria-label="Close">
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <path d="M18 6L6 18M6 6l12 12" />
            </svg>
          </button>
        </div>
        <label className="chat-agent-modal-label" htmlFor="create-project-name">Project name</label>
        <input
          id="create-project-name"
          type="text"
          className="chat-agent-modal-input"
          placeholder="e.g. LNAT prep"
          value={name}
          onChange={(e) => onNameChange(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === 'Enter' && trimmed) { e.preventDefault(); onCreate(); }
            if (e.key === 'Escape') { e.preventDefault(); onClose(); }
          }}
          autoFocus
        />
        <div className="chat-agent-modal-info">
          <span className="chat-agent-modal-info-icon" aria-hidden="true">💡</span>
          <p>
            Projects keep chats in one place. Use them for ongoing work, or just to keep things tidy.
          </p>
        </div>
        <div className="chat-agent-modal-footer">
          <button
            type="button"
            className="chat-agent-modal-create-btn"
            disabled={!trimmed || creating}
            onClick={onCreate}
          >
            Create project
          </button>
        </div>
      </div>
    </div>
  );
}

function SessionContextMenu({
  session,
  projects,
  open,
  moveOpen,
  onToggleMove,
  onRename,
  onMove,
  onDelete,
  onTogglePin,
  onCreateProject,
  onClose,
}) {
  if (!open) return null;
  const isPinned = Boolean(session.pinned_at) || session.session_type === 'onboarding';
  return (
    <div className="chat-agent-context-menu" role="menu">
      <button type="button" className="chat-agent-context-item" role="menuitem" onClick={() => { onRename(session); onClose(); }}>
        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
          <path d="M12 20h9M16.5 3.5a2.12 2.12 0 013 3L7 19l-4 1 1-4L16.5 3.5z" />
        </svg>
        Rename
      </button>
      {session.session_type !== 'onboarding' && (
        <button type="button" className="chat-agent-context-item" role="menuitem" onClick={() => { onTogglePin(session); onClose(); }}>
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <path d="M12 17v5M8 3h8l-1 7 3 3H6l3-3-1-7z" />
          </svg>
          {isPinned ? 'Unpin' : 'Pin'}
        </button>
      )}
      <div className="chat-agent-context-submenu-wrap">
        <button
          type="button"
          className={`chat-agent-context-item${moveOpen ? ' open' : ''}`}
          role="menuitem"
          onClick={onToggleMove}
        >
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <path d="M22 19a2 2 0 01-2 2H4a2 2 0 01-2-2V5a2 2 0 012-2h5l2 3h9a2 2 0 012 2z" />
          </svg>
          Move to project
          <svg className={`chat-agent-context-chevron${moveOpen ? ' expanded' : ''}`} width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <path d="M9 18l6-6-6-6" />
          </svg>
        </button>
        {moveOpen && (
          <div className="chat-agent-context-inline-submenu" role="menu">
            <button type="button" className="chat-agent-context-item chat-agent-context-subitem" onClick={() => { onMove(session, null); onClose(); }}>
              None (Recents)
            </button>
            {projects.map((p) => (
              <button
                key={p.id}
                type="button"
                className="chat-agent-context-item chat-agent-context-subitem"
                onClick={() => { onMove(session, p.id); onClose(); }}
              >
                {p.name}
              </button>
            ))}
            <div className="chat-agent-context-divider" />
            <button
              type="button"
              className="chat-agent-context-item chat-agent-context-subitem"
              onClick={() => { onCreateProject(session); onClose(); }}
            >
              + New project…
            </button>
          </div>
        )}
      </div>
      {session.session_type !== 'onboarding' && (
        <>
          <div className="chat-agent-context-divider" />
          <button type="button" className="chat-agent-context-item danger" role="menuitem" onClick={() => { onDelete(session.id); onClose(); }}>
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <path d="M3 6h18M8 6V4h8v2M19 6l-1 14H6L5 6" />
            </svg>
            Delete
          </button>
        </>
      )}
    </div>
  );
}

function SessionRow({
  session,
  active,
  loading,
  renamingSessionId,
  renameDraft,
  projects,
  menuOpen,
  moveMenuOpen,
  onSelect,
  onDelete,
  onTogglePin,
  onStartRename,
  onRenameDraftChange,
  onCommitRename,
  onCancelRename,
  onMoveToProject,
  onCreateProjectFromMenu,
  onToggleMenu,
  onToggleMoveMenu,
  onCloseMenu,
}) {
  const isRenaming = renamingSessionId === session.id;

  return (
    <div className={`chat-agent-sidebar-item-wrap glass-pill ${active ? 'active' : ''}`}>
      {isRenaming ? (
        <input
          type="text"
          className="chat-agent-sidebar-rename-input"
          value={renameDraft}
          onChange={(e) => onRenameDraftChange(e.target.value)}
          onBlur={onCommitRename}
          onKeyDown={(e) => {
            if (e.key === 'Enter') { e.preventDefault(); onCommitRename(); }
            if (e.key === 'Escape') { e.preventDefault(); onCancelRename(); }
          }}
          autoFocus
          aria-label="Rename chat"
        />
      ) : (
        <button
          type="button"
          className="chat-agent-sidebar-item"
          onClick={() => onSelect(session.id)}
          disabled={loading}
        >
          <span className="chat-agent-sidebar-item-title" title={session.title || 'New chat'}>{session.title || 'New chat'}</span>
        </button>
      )}
      {!isRenaming && (
        <div className="chat-agent-sidebar-item-menu-wrap">
          <button
            type="button"
            className="chat-agent-sidebar-item-menu-btn"
            aria-label="Chat options"
            aria-expanded={menuOpen}
            onClick={(e) => { e.stopPropagation(); onToggleMenu(session.id); }}
          >
            <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor">
              <circle cx="5" cy="12" r="1.5" />
              <circle cx="12" cy="12" r="1.5" />
              <circle cx="19" cy="12" r="1.5" />
            </svg>
          </button>
          {menuOpen && (
            <SessionContextMenu
              session={session}
              projects={projects}
              open={menuOpen}
              moveOpen={moveMenuOpen}
              onToggleMove={onToggleMoveMenu}
              onRename={onStartRename}
              onMove={onMoveToProject}
              onCreateProject={onCreateProjectFromMenu}
              onDelete={onDelete}
              onTogglePin={onTogglePin}
              onClose={onCloseMenu}
            />
          )}
        </div>
      )}
    </div>
  );
}

const processImageFile = (file) => {
  return new Promise((resolve) => {
    const reader = new FileReader();
    reader.onload = (e) => {
      const img = new Image();
      img.onload = () => {
        const maxDim = 1024;
        let width = img.width;
        let height = img.height;
        if (width > maxDim || height > maxDim) {
          if (width > height) {
            height = Math.round((height * maxDim) / width);
            width = maxDim;
          } else {
            width = Math.round((width * maxDim) / height);
            height = maxDim;
          }
          const canvas = document.createElement('canvas');
          canvas.width = width;
          canvas.height = height;
          const ctx = canvas.getContext('2d');
          ctx.drawImage(img, 0, 0, width, height);
          resolve(canvas.toDataURL('image/jpeg', 0.8));
        } else {
          resolve(e.target.result);
        }
      };
      img.src = e.target.result;
    };
    reader.readAsDataURL(file);
  });
};

const renderUserMessage = (text) => {
  if (!text) return null;
  const attachIdx = text.indexOf('[Attached Image');
  const fileIdx = text.indexOf('[Attached File:');
  let mainText = text;
  let hasAttachments = false;
  let attachments = [];
  let splitIdx = -1;
  if (attachIdx !== -1 && fileIdx !== -1) {
    splitIdx = Math.min(attachIdx, fileIdx);
  } else if (attachIdx !== -1) {
    splitIdx = attachIdx;
  } else if (fileIdx !== -1) {
    splitIdx = fileIdx;
  }
  if (splitIdx !== -1) {
    mainText = text.substring(0, splitIdx).trim();
    hasAttachments = true;
    
    // Extract base64 images
    const imageRegex = /\[Attached (?:Image|File) \d+ \(([^\)]+)\)\]:\s*(data:image\/[a-zA-Z\-\+\.]+;base64,[A-Za-z0-9\+\/=\s\r\n]+)/g;
    let match;
    while ((match = imageRegex.exec(text)) !== null) {
      attachments.push({ name: match[1], type: 'image', data: match[2].trim() });
    }
    
    // Extract documents
    const docRegex = /\[Attached File:\s*([^\s\)]+)\s*\((\d+)\s*bytes\)\]/g;
    let docMatch;
    while ((docMatch = docRegex.exec(text)) !== null) {
      attachments.push({ name: docMatch[1], type: 'document', size: parseInt(docMatch[2], 10) });
    }
  }
  return (
    <div className="chat-agent-user-msg-body">
      {mainText && <div className="chat-agent-msg-text-content">{mainText}</div>}
      {hasAttachments && attachments.length > 0 && (
        <div className="chat-agent-msg-attachments">
          {attachments.map((att, idx) => (
            <div key={idx} className="chat-agent-msg-attachment-bubble">
              {att.type === 'image' ? (
                <div className="chat-agent-msg-attachment-img-preview">
                  <img src={att.data} alt={att.name} className="chat-agent-bubble-img" />
                  <span className="chat-agent-bubble-filename">{att.name}</span>
                </div>
              ) : (
                <div className="chat-agent-msg-attachment-doc-preview">
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" className="chat-agent-doc-icon">
                    <path d="M14 2H6a2 2 0 00-2 2v16a2 2 0 002 2h12a2 2 0 002-2V8z" />
                    <polyline points="14 2 14 8 20 8" />
                  </svg>
                  <span className="chat-agent-bubble-filename">{att.name}</span>
                </div>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

// composerLockedOverride/composerLockReasonOverride: only meaningful for
// callers that want to force the locked visual without real onboarding
// data (e.g. ChatShell's stage-4 proof-of-mechanism). When the currently
// open session is the onboarding one and incomplete, the component
// computes its own lock state from real GET /api/onboarding/steps|state
// data instead — see onboardingLocked below.
function ChatAgentPanel({
  onOpenGuide,
  fullPage,
  onClose,
  composerLocked: composerLockedOverride = false,
  composerLockReason: composerLockReasonOverride,
  onOnboardingComplete,
  // ChatShell only: false while Apps/Cases is showing so empty drafts can be
  // discarded; undefined outside the shell (e.g. AppStoreView) skips that.
  isChatTabActive,
  // ChatShell only: incremented for an explicit startNewChat request.
  // Returning to the Chat tab does not increment this.
  newChatRequest = 0,
  // ChatShell only: incremented when Profile's "Redo onboarding" / "Continue
  // redo" asks for the onboarding chat. An open redo never changes which chat
  // /chat opens by default; only this explicit request opens it.
  onboardingChatRequest = 0,
  // ChatShell only: Paths (My Path folder, ?path= / ?plan= views) lives in /chat.
  pathsEnabled = false,
}) {
  const { user } = useAuth();
  // Cosmetic only: the backend decides instructor mode from the database.
  // The empty state waits until onboarding is complete; staff answer the
  // school_staff set, and the resolver picks their questions.
  const isInstructor = isInstructorAccount(user);
  const navigate = useNavigate();
  const textareaRef = useRef(null);
  const brandName = isPathbuddyHost() ? 'Pathbuddy' : 'IVORI';
  const brandEduLabel = isPathbuddyHost() ? 'Pathbuddy' : null;

  const [agentType, setAgentType] = useState('planner');
  const autoMode = 'default';
  const [selectedModel, setSelectedModel] = useState('uai-default');
  const [sessions, setSessions] = useState([]);
  const [pinnedSessions, setPinnedSessions] = useState([]);
  const [projects, setProjects] = useState([]);
  const [unassignedRevealCount, setUnassignedRevealCount] = useState(30);
  const [loadingMoreSessions, setLoadingMoreSessions] = useState(false);
  const [lastSessionsFetchFull, setLastSessionsFetchFull] = useState(false);

  // Onboarding: only relevant when the open session's session_type is
  // 'onboarding' and it hasn't completed. onboardingSteps/onboardingAnswers
  // are refetched whenever the active session or its message list changes,
  // so answering a step (which appends new chat_messages rows) recomputes
  // which step is next.
  const [onboardingSteps, setOnboardingSteps] = useState(null);
  const [onboardingAnswers, setOnboardingAnswers] = useState(null);
  const [onboardingCompletedAt, setOnboardingCompletedAt] = useState(null);
  // Set only after a successful state read says onboarding is finished.
  // Instructor chips stay hidden until then, including when the check fails.
  const [onboardingKnownComplete, setOnboardingKnownComplete] = useState(false);
  const [onboardingSaving, setOnboardingSaving] = useState(false);
  // The redo fields of GET /api/onboarding/state (absent from an older
  // backend, which reads as no redo, none ever and no invitation): the open
  // redo (its answers are this round's only), every redo (for the dividers)
  // and the "new questions" invitation.
  const [onboardingRedo, setOnboardingRedo] = useState(null);
  const [onboardingRedos, setOnboardingRedos] = useState([]);
  const [onboardingInvitation, setOnboardingInvitation] = useState(null);
  const [expandedProjects, setExpandedProjects] = useState({});
  // Open on desktop (>768px). On mobile (<=768px) it is a drawer over the
  // content, so it starts closed. The resize guard below still auto-closes
  // when the viewport narrows past that breakpoint.
  const [sidebarOpen, setSidebarOpen] = useState(
    () => typeof window !== 'undefined' && window.innerWidth > 768
  );
  // True only while the drawer is closed because the resize guard below
  // auto-closed it while narrowing past 768px -- never set by an explicit
  // user action. Cleared by setSidebarOpenExplicit (every real open/close:
  // buttons, scrim, Escape, session/project select, new chat) so an
  // explicit choice always wins over the auto-close guard's memory of it.
  const autoClosedRef = useRef(false);
  const sidebarOpenRef = useRef(sidebarOpen);
  sidebarOpenRef.current = sidebarOpen;
  const setSidebarOpenExplicit = (value) => {
    autoClosedRef.current = false;
    setSidebarOpen(value);
  };
  const [sessionId, setSessionId] = useState(null);
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState('');
  const [discussionMode, setDiscussionMode] = useState(false);
  const [loading, setLoading] = useState(false);
  const [awaitingBotReply, setAwaitingBotReply] = useState(false);
  const [animatingBotMessageId, setAnimatingBotMessageId] = useState(null);
  const [streamingBotMessageId, setStreamingBotMessageId] = useState(null);
  const [streamPaused, setStreamPaused] = useState(false);
  const [showCreditHeader, setShowCreditHeader] = useState(true);

  // Auto-grow textarea height to fit content, with a max-height limit of 240px
  useEffect(() => {
    if (textareaRef.current) {
      textareaRef.current.style.height = 'auto';
      textareaRef.current.style.height = `${Math.min(textareaRef.current.scrollHeight, 240)}px`;
    }
  }, [input]);

  // Narrowing past the mobile breakpoint force-closes the drawer (otherwise
  // a sidebar left open from desktop width would land in the mobile crop),
  // and marks the close as automatic via autoClosedRef. Widening back out
  // only re-opens it if that ref is still set -- i.e. only if nothing
  // explicit (a button, scrim, Escape, selecting a chat) happened in
  // between. That keeps: explicitly open on desktop then narrow-then-wide
  // restores open; already closed then narrow-then-wide stays closed;
  // explicitly opened while narrow then widened stays open.
  useEffect(() => {
    let wasDesktop = window.innerWidth > 768;
    const handleResize = () => {
      const isDesktop = window.innerWidth > 768;
      if (wasDesktop && !isDesktop) {
        // Only the crossing that actually closes an open sidebar counts as
        // "automatic" -- if it was already closed (explicitly, before this
        // resize), leave autoClosedRef alone so that explicit choice is
        // what widening back out sees, not this no-op crossing.
        if (sidebarOpenRef.current) autoClosedRef.current = true;
        setSidebarOpen(false);
      } else if (!wasDesktop && isDesktop && autoClosedRef.current) {
        autoClosedRef.current = false;
        setSidebarOpen(true);
      }
      wasDesktop = isDesktop;
    };
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);
  
  const promptCardRef = useRef(null);
  const [attachedFiles, setAttachedFiles] = useState([]);
  const [fileUploading, setFileUploading] = useState(false);
  const fileInputRef = useRef(null);

  const handleFileChange = async (e) => {
    const files = Array.from(e.target.files || []);
    if (files.length === 0) return;

    for (const file of files) {
      if (file.type.startsWith('image/')) {
        setFileUploading(true);
        try {
          const base64 = await processImageFile(file);
          setAttachedFiles((prev) => [
            ...prev,
            {
              name: file.name,
              type: 'image',
              size: file.size,
              base64: base64,
            },
          ]);
        } catch (err) {
          setError('Failed to process image.');
        } finally {
          setFileUploading(false);
        }
      } else {
        setFileUploading(true);
        setError('');
        try {
          const res = await chatAPI.uploadFile(file);
          if (res && res.text !== undefined) {
            setAttachedFiles((prev) => [
              ...prev,
              {
                name: res.filename || file.name,
                type: 'document',
                size: res.size || file.size,
                text: res.text,
              },
            ]);
          } else {
            setError(`Failed to parse file ${file.name}`);
          }
        } catch (err) {
          const apiMsg = err?.data?.error || err?.message || 'Failed to upload and parse file.';
          setError(apiMsg);
        } finally {
          setFileUploading(false);
        }
      }
    }
    e.target.value = '';
  };

  const removeAttachedFile = (index) => {
    setAttachedFiles((prev) => prev.filter((_, i) => i !== index));
  };
  const [error, setError] = useState('');
  const [searchChats, setSearchChats] = useState('');
  const [renamingSessionId, setRenamingSessionId] = useState(null);
  const [renameDraft, setRenameDraft] = useState('');
  const [renamingProjectId, setRenamingProjectId] = useState(null);
  const [renameProjectDraft, setRenameProjectDraft] = useState('');
  const [createProjectOpen, setCreateProjectOpen] = useState(false);
  const [newProjectName, setNewProjectName] = useState('');
  const [creatingProject, setCreatingProject] = useState(false);
  const [activeProjectViewId, setActiveProjectViewId] = useState(null);
  const [openSessionMenuId, setOpenSessionMenuId] = useState(null);
  const [moveSubmenuOpen, setMoveSubmenuOpen] = useState(false);
  const [pendingMoveSessionId, setPendingMoveSessionId] = useState(null);
  const [projectPreviews, setProjectPreviews] = useState({});
  const [reasoningMode, setReasoningMode] = useState(() => localStorage.getItem('chat-reasoning-mode') === 'true');
  // True after the mount-time session bootstrap finishes — footer "new chat"
  // requests wait on this so they don't no-op while init still holds loading.
  const [chatReady, setChatReady] = useState(false);
  const messagesEndRef = useRef(null);
  const panelRef = useRef(null);
  const handleNewSessionRef = useRef(null);
  const discardEmptyDraftRef = useRef(null);
  // Only auto-delete sessions we know are unused drafts (created/reused as
  // empty). Avoids wiping a real chat whose messages have not loaded yet.
  const disposableDraftIdRef = useRef(null);

  const hasUserMessages = messages.some((m) => m.sender === 'user');
  const greeting = getTimeBasedGreeting(user);

  const filterBySearch = useCallback(
    (list) => (searchChats.trim()
      ? list.filter((s) => (s.title || '').toLowerCase().includes(searchChats.trim().toLowerCase()))
      : list),
    [searchChats]
  );

  // Mirrors the backend's pinned derivation exactly (chatbot_service.py's
  // get_user_chat_sessions): a user-initiated pin (pinned_at set) or the
  // onboarding session (always pinned, never written to pinned_at).
  const isPinnedSession = (s) => Boolean(s.pinned_at) || s.session_type === 'onboarding';
  const hideFromSidebar = (s) => shouldHideSessionFromSidebar(s, {
    activeSessionId: sessionId,
    activeHasUserMessages: hasUserMessages,
  });
  const unassignedSessions = filterBySearch(
    sessions.filter((s) => !s.project_id && !isPinnedSession(s) && !hideFromSidebar(s))
  );
  const visibleUnassignedSessions = unassignedSessions.slice(0, unassignedRevealCount);
  const canShowMoreSessions = unassignedRevealCount < unassignedSessions.length
    || (lastSessionsFetchFull && unassignedRevealCount >= unassignedSessions.length);
  const activeProject = projects.find((p) => p.id === activeProjectViewId) || null;
  const activeProjectSessions = activeProject
    ? filterBySearch(sessions.filter((s) => s.project_id === activeProject.id && !hideFromSidebar(s)))
    : [];
  const inProjectView = fullPage && !!activeProjectViewId && !!activeProject;

  const refreshSidebar = useCallback(async () => {
    let sessionList = [];
    let pinnedList = [];
    let projectList = [];
    try {
      const res = await chatAPI.getAllSessions();
      sessionList = Array.isArray(res) ? res : [];
    } catch (_) {
      sessionList = [];
    }
    try {
      const res = await chatAPI.getChatSessions({ pinned_only: true });
      pinnedList = Array.isArray(res) ? res : [];
    } catch (_) {
      pinnedList = [];
    }
    try {
      const res = await chatAPI.getProjects();
      projectList = Array.isArray(res) ? res : [];
    } catch (_) {
      projectList = [];
    }
    setSessions(sessionList);
    setPinnedSessions(pinnedList);
    setProjects(projectList);
    setUnassignedRevealCount(30);
    setLastSessionsFetchFull(sessionList.length >= 100);
    return sessionList;
  }, []);

  const handleShowMoreSessions = useCallback(async () => {
    if (unassignedRevealCount < unassignedSessions.length) {
      setUnassignedRevealCount((c) => c + 20);
      return;
    }
    if (!lastSessionsFetchFull || loadingMoreSessions) return;
    setLoadingMoreSessions(true);
    try {
      const res = await chatAPI.getChatSessions({
        limit: 100,
        offset: sessions.length,
        unassigned_only: true,
        exclude_pinned: true,
      });
      const more = Array.isArray(res) ? res : [];
      if (more.length > 0) {
        setSessions((prev) => [...prev, ...more]);
        setUnassignedRevealCount((c) => c + more.length);
      }
      setLastSessionsFetchFull(more.length >= 100);
    } catch (_) {
      // leave state as-is; user can retry by clicking Show more again
    } finally {
      setLoadingMoreSessions(false);
    }
  }, [unassignedRevealCount, unassignedSessions.length, lastSessionsFetchFull, loadingMoreSessions, sessions.length]);

  const messageCacheRef = useRef({});
  const streamAbortRef = useRef(null);
  const streamTextRef = useRef('');
  const streamGapTimerRef = useRef(null);

  const loadMessagesForSession = useCallback(async (id, forceRefresh = false) => {
    if (!id) {
      setMessages([]);
      return false;
    }
    setAwaitingBotReply(false);
    setAnimatingBotMessageId(null);
    setStreamingBotMessageId(null);
    setStreamPaused(false);
    streamTextRef.current = '';
    if (streamGapTimerRef.current) {
      window.clearTimeout(streamGapTimerRef.current);
      streamGapTimerRef.current = null;
    }

    // Instant zero-latency render from cache if available
    if (messageCacheRef.current[id] && !forceRefresh) {
      setMessages(messageCacheRef.current[id]);
    }

    try {
      const list = await chatAPI.getChatMessages(id);
      const validList = Array.isArray(list) ? list : [];
      messageCacheRef.current[id] = validList;
      setMessages(validList);
      setError('');
      return true;
    } catch (_) {
      if (!messageCacheRef.current[id]) {
        setMessages([]);
        setError('Could not load this chat.');
      }
      return false;
    }
  }, []);

  const scrollToBottom = useCallback(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'auto' });
  }, []);

  const handleTypewriterProgress = useCallback(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'auto' });
  }, []);

  const handleTypewriterComplete = useCallback(() => {
    setAnimatingBotMessageId(null);
    scrollToBottom();
  }, [scrollToBottom]);

  useEffect(() => {
    let cancelled = false;
    const init = async () => {
      setError('');
      try {
        // Onboarding completion must be checked unconditionally, not only
        // when the session list happens to come back empty — getAllSessions
        // excludes the onboarding session itself (it's always pinned), so a
        // user with onboarding incomplete AND any other session would never
        // reach this check at all if it were nested inside "list is empty".
        // Onboarding is mandatory (decision 6) and must not be skippable
        // this way. Run both requests together rather than sequentially:
        // for the majority of users (already onboarded), the sidebar result
        // is what's actually used, and gating it behind a first awaited
        // getState() call would add a pure-latency round trip with no
        // correctness benefit for them.
        const [listSafe, onboardingState] = await Promise.all([
          refreshSidebar(),
          onboardingAPI.getState().catch(() => null), // null = "check failed", not "complete"
        ]);
        if (cancelled) return;

        let onboardingIncomplete = onboardingState === null ? null : !onboardingState.onboarding_completed_at;

        if (onboardingIncomplete === null) {
          // The completion check itself failed (network blip, transient
          // 401 right after signup, etc). Retry once before falling
          // through — silently defaulting to "treat as complete" is
          // exactly the bug this fix exists for. A second failure
          // surfaces as the existing generic error state rather than
          // guessing either way.
          try {
            const retryState = await onboardingAPI.getState();
            onboardingIncomplete = !retryState.onboarding_completed_at;
          } catch (_) {
            setError('Could not load chat.');
            return;
          }
          if (cancelled) return;
        }

        if (onboardingIncomplete) {
          const ensured = await onboardingAPI.ensureSession();
          if (cancelled) return;
          setSessionId(ensured?.session_id || null);
          if (ensured?.session_id) {
            await loadMessagesForSession(ensured.session_id);
          } else {
            setMessages([]);
          }
          await refreshSidebar();
          return;
        }

        setOnboardingKnownComplete(true);

        // Onboarding confirmed complete — restore the most recent session,
        // or start a fresh one if the user has none.
        if (listSafe.length > 0) {
          const first = listSafe[0];
          setSessionId(first.id);
          if (first.agent_type) setAgentType(first.agent_type);
          if (first.selected_model) setSelectedModel(first.selected_model);
          else setSelectedModel('uai-default');
          await loadMessagesForSession(first.id);
        } else {
          const created = await chatAPI.createChatSession('New chat', 'planner');
          if (cancelled) return;
          const createdId = created?.id || null;
          setSessionId(createdId);
          setMessages([]);
          if (createdId) disposableDraftIdRef.current = createdId;
          await refreshSidebar();
        }
      } catch (e) {
        if (!cancelled) {
          setError('Could not load chat.');
          setSessionId(null);
          setMessages([]);
        }
      } finally {
        if (!cancelled) {
          setLoading(false);
          setChatReady(true);
        }
      }
    };
    init();
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    scrollToBottom();
  }, [messages, awaitingBotReply, streamingBotMessageId, streamPaused, scrollToBottom]);

  // sessions excludes pinned and onboarding rows (getAllSessions passes
  // exclude_pinned) — anything looking up a session by id, regardless of
  // its pinned/onboarding status, needs both lists. Use this, not `sessions`
  // alone, for any future by-id lookup.
  const allSessions = [...sessions, ...pinnedSessions];
  const activeSession = allSessions.find((s) => s.id === sessionId);
  // Paths inside /chat (features/paths): owns its own state and URL; this
  // panel only mounts it and routes the composer to it while it is active.
  const paths = usePathsChat({
    enabled: pathsEnabled && Boolean(fullPage),
    chatReady,
    chatSessionId: sessionId,
    setComposerText: setInput,
    focusComposer: () => textareaRef.current?.focus(),
    onShowMyPath: () => setSidebarOpenExplicit(true),
  });
  // Deliberately NOT gated on !onboardingCompletedAt: completion is now
  // recorded when the 'summary' step itself is answered (the user
  // acknowledging the summary screen), not on the second-to-last step
  // ('pathways'). Gating card-rendering on "not yet complete" made the
  // summary screen structurally unreachable — the instant the data became
  // complete, this whole flag (and everything derived from it) would flip
  // false in the same render the summary was supposed to appear in. Now
  // the only thing that stops rendering onboarding UI is running out of
  // unanswered steps, which happens naturally once summary itself is
  // answered.
  const isOnboardingSession = activeSession?.session_type === 'onboarding';

  // Once the user sends anything, the open draft is no longer disposable.
  useEffect(() => {
    if (hasUserMessages && sessionId && disposableDraftIdRef.current === sessionId) {
      disposableDraftIdRef.current = null;
    }
  }, [hasUserMessages, sessionId]);

  // Every GET /api/onboarding/state result goes through here, so the redo
  // fields are never stale against the answers.
  const applyOnboardingState = useCallback((stateRes) => {
    setOnboardingAnswers(stateRes.answers || {});
    setOnboardingCompletedAt(stateRes.onboarding_completed_at || null);
    if (stateRes.onboarding_completed_at) setOnboardingKnownComplete(true);
    setOnboardingRedo(stateRes.redo || null);
    setOnboardingRedos(Array.isArray(stateRes.redos) ? stateRes.redos : []);
    setOnboardingInvitation(stateRes.invitation || null);
  }, []);

  // Refetch the step catalogue + state whenever the open session is the
  // onboarding one — including after every answer, since saving one
  // appends new chat_messages rows.
  useEffect(() => {
    if (!isOnboardingSession) return;
    let cancelled = false;
    const load = async () => {
      try {
        const [stepsRes, stateRes] = await Promise.all([
          onboardingAPI.getSteps(),
          onboardingAPI.getState(),
        ]);
        if (cancelled) return;
        setOnboardingSteps(stepsRes.steps);
        applyOnboardingState(stateRes);
      } catch (_) {
        // Leave prior onboarding state in place on a transient failure —
        // the composer stays locked (safe default) rather than silently
        // unlocking on an error.
      }
    };
    load();
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOnboardingSession, sessionId, messages.length]);

  // Progress is measured against this round's answers: during a redo only
  // the answers given since it started (so every step is asked again, with
  // nothing pre-filled), otherwise every saved answer.
  const onboardingRoundAnswers = (onboardingRedo ? onboardingRedo.answers : onboardingAnswers) || {};
  const onboardingActiveSteps = onboardingSteps ? activeOnboardingSteps(onboardingSteps, onboardingRoundAnswers) : [];
  // The next unanswered step, INCLUDING 'summary' — it's a real,
  // answerable step now (its answer is the acknowledgement), not excluded
  // as a special case. Whichever component renders for it (option card vs.
  // summary card) is decided purely by step.kind below. None once onboarding
  // is complete and no redo is open: a step the user's set gained since is
  // offered through the invitation card (a full redo), not asked on its own.
  const onboardingNextStep = isOnboardingSession && (!onboardingCompletedAt || onboardingRedo)
    ? onboardingActiveSteps.find((s) => onboardingRoundAnswers[s.id] === undefined)
    : null;
  const onboardingStepIndex = onboardingNextStep ? onboardingActiveSteps.findIndex((s) => s.id === onboardingNextStep.id) : -1;

  const handleOnboardingAnswer = useCallback(async (value) => {
    if (!onboardingNextStep || onboardingSaving) return;
    setOnboardingSaving(true);
    try {
      const saved = await onboardingAPI.saveAnswer(onboardingNextStep.id, value);
      await loadMessagesForSession(sessionId, true);
      const stateRes = await onboardingAPI.getState();
      applyOnboardingState(stateRes);
      // Notify the parent (ChatShell) the instant completion happens,
      // rather than have it wait for its own reload/poll — this is what
      // actually unlocks the tab bar in real time. Keyed on this save
      // completing onboarding (the summary step, first time or a redo's),
      // not on onboarding_completed_at, which a redo never clears.
      if (saved?.onboarding_completed) {
        onOnboardingComplete?.();
      }
    } catch (_) {
      setError('Could not save that answer. Please try again.');
    } finally {
      setOnboardingSaving(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [onboardingNextStep, onboardingSaving, sessionId, loadMessagesForSession, onOnboardingComplete, applyOnboardingState]);

  // The summary step's "Set up my workspace" IS the real save now — not a
  // client-side-only transition. Reuses handleOnboardingAnswer exactly like
  // any other step (same save/refetch/notify-parent path); the fixed
  // acknowledgement literal must match ONBOARDING_SUMMARY_ACK in
  // onboarding_service.py.
  const handleOnboardingFinish = useCallback(() => {
    handleOnboardingAnswer(ONBOARDING_SUMMARY_ACK);
  }, [handleOnboardingAnswer]);

  // Summary-card edit mode: saves ONE already-answered step by explicit
  // step_id, unlike handleOnboardingAnswer (which always targets
  // onboardingNextStep — undefined once onboarding is complete, since there
  // is no next unanswered step left). Same save/refetch shape as
  // handleOnboardingAnswer, minus the onOnboardingComplete notification
  // (editing a past answer never (re)completes onboarding — see
  // onboarding_service.save_answer's already_completed guard).
  const handleOnboardingEditSave = useCallback(async (stepId, value) => {
    await onboardingAPI.saveAnswer(stepId, value);
    await loadMessagesForSession(sessionId, true);
    const stateRes = await onboardingAPI.getState();
    applyOnboardingState(stateRes);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sessionId, loadMessagesForSession]);

  // The invitation card's two actions. A failure rejects, and the card keeps
  // itself with an error. "Answer them now" starts a full redo: the server
  // writes the divider and the first step's prompt into this chat, so the
  // transcript is reloaded, then state (now with the open redo). Apps and
  // Cases stay unlocked: a redo never clears onboarding_completed_at.
  const handleInvitationAccept = useCallback(async () => {
    const started = await onboardingAPI.startRedo();
    // The redo exists from here on, so nothing below reports a failure to
    // the card: the redo is shown at once from the start response, then the
    // transcript (divider, first question) and state are refetched.
    if (started?.redo) {
      setOnboardingInvitation(null);
      setOnboardingRedo({ ...started.redo, answers: {} });
      setOnboardingRedos((prev) => (
        prev.some((r) => r.id === started.redo.id) ? prev : [...prev, started.redo]
      ));
    }
    try {
      await loadMessagesForSession(sessionId, true);
      applyOnboardingState(await onboardingAPI.getState());
    } catch (e) {
      console.error('Could not refresh the onboarding chat after starting the redo:', e);
    }
  }, [sessionId, loadMessagesForSession, applyOnboardingState]);

  const handleInvitationDismiss = useCallback(async () => {
    await onboardingAPI.dismissInvitation(onboardingInvitation?.set_key);
    applyOnboardingState(await onboardingAPI.getState());
  }, [onboardingInvitation, applyOnboardingState]);

  // Ids of the stored messages each redo starts with, drawn as dividers.
  const onboardingDividerIds = new Set(
    isOnboardingSession ? onboardingRedos.map((r) => r.divider_message_id).filter(Boolean).map(String) : []
  );

  // Per the design decision: once onboarding is complete, this session is a
  // permanent settings surface, not a conversation — the summary card stays
  // pinned (editable) below the transcript forever, and the composer's text
  // input/send row is replaced by a pointer to start a new chat instead of
  // ordinary locked-composer copy (which would wrongly imply there's still
  // something above to answer). The plus-menu row is unaffected either way.
  // An open redo is not "complete" even though onboarding_completed_at stays
  // set: the live card, the summary card's gating and the composer lock
  // follow the redo until its own summary is answered.
  const onboardingFullyComplete = isOnboardingSession && Boolean(onboardingCompletedAt) && !onboardingRedo;

  const composerLocked = isOnboardingSession
    ? (Boolean(onboardingNextStep) || onboardingSaving) && !onboardingFullyComplete
    : composerLockedOverride;
  const composerLockReason = isOnboardingSession
    ? 'Answer above to continue'
    : composerLockReasonOverride;

  useEffect(() => {
    const handleClickOutside = (e) => {
      // Close session action menus when clicking outside
      if (!e.target.closest('.chat-agent-sidebar-item-menu-wrap') && !e.target.closest('.chat-agent-sidebar-item-wrap')) {
        setOpenSessionMenuId(null);
        setMoveSubmenuOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Escape closes the mobile sidebar drawer. Desktop's sidebar isn't a
  // dismissible overlay, so this only acts at/below the mobile breakpoint.
  useEffect(() => {
    if (!sidebarOpen) return;
    const handleKeyDown = (e) => {
      if (e.key === 'Escape' && window.innerWidth <= 768) {
        setSidebarOpenExplicit(false);
      }
    };
    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [sidebarOpen]);

  useEffect(() => {
    if (!activeProjectViewId) return;
    const projectSessions = sessions.filter((s) => s.project_id === activeProjectViewId);
    if (projectSessions.length === 0) return;
    let cancelled = false;
    const loadPreviews = async () => {
      const previews = {};
      await Promise.all(
        projectSessions.slice(0, 30).map(async (s) => {
          try {
            const msgs = await chatAPI.getChatMessages(s.id);
            const first = Array.isArray(msgs) ? msgs.find((m) => m.sender === 'user') || msgs[0] : null;
            if (first?.message) previews[s.id] = first.message.slice(0, 120);
          } catch (_) {}
        })
      );
      if (!cancelled) setProjectPreviews((prev) => ({ ...prev, ...previews }));
    };
    loadPreviews();
    return () => { cancelled = true; };
  }, [activeProjectViewId, sessions]);

  const handleSend = async (overrideText) => {
    const raw = overrideText != null && typeof overrideText === 'string'
      ? overrideText
      : input;
    const text = (raw ?? '').trim();
    if (paths.active) {
      if (!text || !paths.composerReady) return;
      if (!overrideText) setInput('');
      await paths.send(text);
      return;
    }
    if ((!text && attachedFiles.length === 0) || !sessionId || loading) return;
    if (!overrideText) setInput('');
    setLoading(true);
    setError('');
    setAnimatingBotMessageId(null);
    setStreamingBotMessageId(null);
    setStreamPaused(false);
    streamTextRef.current = '';
    if (streamGapTimerRef.current) {
      window.clearTimeout(streamGapTimerRef.current);
      streamGapTimerRef.current = null;
    }
    setAwaitingBotReply(true);

    streamAbortRef.current?.abort();
    const streamAbort = new AbortController();
    streamAbortRef.current = streamAbort;

    // Build user message body with attachments
    let finalMessageText = text;
    const images = attachedFiles.filter(f => f.type === 'image');
    const docs = attachedFiles.filter(f => f.type === 'document');

    if (images.length > 0) {
      images.forEach((img, idx) => {
        finalMessageText += `\n\n[Attached Image ${idx + 1} (${img.name})]:\n${img.base64}`;
      });
    }

    if (docs.length > 0) {
      docs.forEach((doc) => {
        finalMessageText += `\n\n[Attached File: ${doc.name} (${doc.size} bytes)]\n---\n${doc.text}\n---`;
      });
    }

    finalMessageText = finalMessageText.trim();

    const filesToClear = [...attachedFiles];
    setAttachedFiles([]);

    const userMsg = { id: `u-${Date.now()}`, sender: 'user', message: finalMessageText, created_at: new Date().toISOString() };
    setMessages((prev) => [...prev, userMsg]);
    const botCountBefore = messages.filter((m) => m.sender === 'bot').length;
    const streamBotId = `stream-${Date.now()}`;
    let streamStarted = false;
    let streamCompleted = false;

    const scheduleStreamGapCheck = () => {
      if (streamGapTimerRef.current) {
        window.clearTimeout(streamGapTimerRef.current);
      }
      streamGapTimerRef.current = window.setTimeout(() => {
        if (!streamCompleted) {
          setStreamPaused(true);
        }
      }, 450);
    };

    const applyStreamDelta = (delta) => {
      streamTextRef.current += delta;
      setStreamPaused(false);
      scheduleStreamGapCheck();
      if (!streamStarted) {
        streamStarted = true;
        setAwaitingBotReply(false);
        setStreamingBotMessageId(streamBotId);
        setMessages((prev) => [
          ...prev,
          {
            id: streamBotId,
            sender: 'bot',
            message: streamTextRef.current,
            created_at: new Date().toISOString(),
          },
        ]);
      } else {
        setMessages((prev) => prev.map((m) => (
          m.id === streamBotId ? { ...m, message: streamTextRef.current } : m
        )));
      }
    };

    const handleSendFailure = async (e) => {
      setAttachedFiles(filesToClear);
      const apiData = e?.data;
      let sendErrorMessage;
      if (apiData?.error === 'rate_limit_exceeded') {
        sendErrorMessage = apiData.message || 'You have reached your daily chat limit. Try again tomorrow.';
      } else if (apiData?.error === 'content_blocked') {
        sendErrorMessage = apiData.message || 'That message cannot be sent.';
      } else {
        const apiMsg = apiData?.error || apiData?.message || e?.message;
        sendErrorMessage = apiMsg && typeof apiMsg === 'string' ? apiMsg : 'Failed to send. Try again.';
      }
      setError(sendErrorMessage);
      if (streamStarted) {
        await loadMessagesForSession(sessionId, true);
      } else {
        setMessages((prev) => prev.filter((m) => m.id !== userMsg.id));
        await loadMessagesForSession(sessionId);
      }
      setError(sendErrorMessage);
    };

    const finishSidebarRefresh = () => {
      void refreshSidebar().then((listSafe) => {
        const current = listSafe.find((s) => s.id === sessionId);
        if (current?.title) {
          setSessions((prev) => prev.map((s) => (s.id === sessionId ? { ...s, title: current.title } : s)));
        }
      });
    };

    try {
      await chatAPI.postMessageStream(
        sessionId,
        finalMessageText,
        {
          autoMode,
          selectedModel,
          reasoningMode,
          signal: streamAbort.signal,
        },
        {
          onToken: (delta) => {
            if (!delta) return;
            applyStreamDelta(delta);
          },
          onPhase: (phase) => {
            if (phase === 'thinking' && streamStarted && !streamCompleted) {
              setStreamPaused(true);
            }
          },
          onDone: (data) => {
            streamCompleted = true;
            if (streamGapTimerRef.current) {
              window.clearTimeout(streamGapTimerRef.current);
              streamGapTimerRef.current = null;
            }
            setStreamPaused(false);

            const finalMsg = data?.bot_message;
            const finalText = data?.message ?? finalMsg?.message;
            const newMessages = Array.isArray(data?.messages) ? data.messages : null;

            if (streamStarted && finalMsg) {
              setMessages((prev) => {
                const merged = prev.map((m) => (
                  m.id === streamBotId
                    ? { ...finalMsg, message: finalText || m.message || '' }
                    : m
                ));
                if (sessionId) {
                  messageCacheRef.current[sessionId] = merged;
                }
                return merged;
              });
            } else if (newMessages) {
              setMessages(newMessages);
              if (sessionId) {
                messageCacheRef.current[sessionId] = newMessages;
              }
            }

            setStreamingBotMessageId(null);
            setAwaitingBotReply(false);
            setLoading(false);
            finishSidebarRefresh();
          },
        }
      );
    } catch (e) {
      if (e?.name === 'AbortError') return;

      if (!streamStarted) {
        try {
          const updated = await chatAPI.postMessage(sessionId, finalMessageText, autoMode, selectedModel, reasoningMode);
          const newMessages = Array.isArray(updated) ? updated : [...messages, userMsg];
          setMessages(newMessages);
          if (sessionId) {
            messageCacheRef.current[sessionId] = newMessages;
          }
          setAwaitingBotReply(false);
          setLoading(false);
          setError('');
          const botMessages = newMessages.filter((m) => m.sender === 'bot');
          if (botMessages.length > botCountBefore) {
            setAnimatingBotMessageId(botMessages[botMessages.length - 1].id);
          }
          finishSidebarRefresh();
          return;
        } catch (fallbackErr) {
          await handleSendFailure(fallbackErr);
        }
      } else {
        await handleSendFailure(e);
      }
    } finally {
      if (streamAbortRef.current === streamAbort) {
        streamAbortRef.current = null;
      }
      if (streamGapTimerRef.current) {
        window.clearTimeout(streamGapTimerRef.current);
        streamGapTimerRef.current = null;
      }
      setLoading(false);
      if (!streamCompleted) {
        setAwaitingBotReply(false);
        setStreamingBotMessageId(null);
        setStreamPaused(false);
      }
    }
  };

  const handleSelectSession = (id) => {
    if (!id) return;
    paths.exit();
    const prevId = sessionId;
    const prevWasEmptyDraft = shouldDiscardEmptyDraftOnSelect({
      prevSessionId: prevId,
      nextSessionId: id,
      messages,
      sessionType: activeSession?.session_type,
    });
    const session = sessions.find((s) => s.id === id) || pinnedSessions.find((s) => s.id === id);
    setActiveProjectViewId(null);
    setSessionId(id);
    if (session?.agent_type) setAgentType(session.agent_type);
    if (session?.selected_model) setSelectedModel(session.selected_model);
    else setSelectedModel('uai-default');

    if (messageCacheRef.current[id]) {
      setMessages(messageCacheRef.current[id]);
    }
    loadMessagesForSession(id);
    // The mobile sidebar is an overlay drawer; picking a chat should return
    // to the conversation instead of leaving the drawer open on top of it.
    if (window.innerWidth <= 768) setSidebarOpenExplicit(false);

    // Drop the abandoned empty draft so it never stacks in Recent.
    if (prevWasEmptyDraft) {
      if (disposableDraftIdRef.current === prevId) disposableDraftIdRef.current = null;
      chatAPI.deleteSession(prevId)
        .then(() => refreshSidebar())
        .catch(() => {});
      delete messageCacheRef.current[prevId];
    }
  };

  const handleOpenProjectView = (projectId) => {
    setOpenSessionMenuId(null);
    setMoveSubmenuOpen(false);
    setActiveProjectViewId(projectId);
    setExpandedProjects((prev) => ({ ...prev, [projectId]: true }));
    if (window.innerWidth <= 768) setSidebarOpenExplicit(false);
  };

  const discardEmptyDraft = async (id, { clearIfActive = true } = {}) => {
    if (!id || disposableDraftIdRef.current !== id) return false;
    const session = sessions.find((s) => s.id === id)
      || pinnedSessions.find((s) => s.id === id)
      || activeSession;
    if (session?.session_type === 'onboarding') return false;
    disposableDraftIdRef.current = null;
    delete messageCacheRef.current[id];
    // Clear local state before the network delete so a quick return to Chat
    // cannot reuse a draft that is mid-deletion.
    if (clearIfActive && sessionId === id) {
      setSessionId(null);
      setMessages([]);
    }
    try {
      await chatAPI.deleteSession(id);
      await refreshSidebar();
      return true;
    } catch (_) {
      return false;
    }
  };
  discardEmptyDraftRef.current = discardEmptyDraft;

  const handleNewSession = async (projectId = null) => {
    if (loading) return;
    paths.exit();

    // Already on an empty top-level draft — reuse it instead of stacking
    // another content-less "New chat" in the sidebar.
    if (shouldReuseEmptyDraft({
      projectId,
      sessionId,
      messages,
      sessionType: activeSession?.session_type,
    })) {
      disposableDraftIdRef.current = sessionId;
      setActiveProjectViewId(null);
      if (window.innerWidth <= 768) setSidebarOpenExplicit(false);
      return;
    }

    setLoading(true);
    setError('');
    try {
      if (sessionId && disposableDraftIdRef.current === sessionId) {
        await discardEmptyDraft(sessionId, { clearIfActive: false });
      }
      const created = await chatAPI.createChatSession('New chat', agentType, projectId);
      const id = created?.id;
      if (!id) {
        setLoading(false);
        return;
      }
      disposableDraftIdRef.current = id;
      await refreshSidebar();
      setActiveProjectViewId(null);
      setSessionId(id);
      setMessages([]);
      if (window.innerWidth <= 768) setSidebarOpenExplicit(false);
    } catch (e) {
      setError('Could not start a new chat.');
    } finally {
      setLoading(false);
    }
  };
  handleNewSessionRef.current = handleNewSession;

  // Explicit startNewChat (via ChatShell): open a fresh draft once bootstrap is done.
  // Returning to the Chat tab does not.
  useEffect(() => {
    if (!chatReady || !newChatRequest) return;
    handleNewSessionRef.current?.();
  }, [chatReady, newChatRequest]);

  // Profile's redo buttons (via ChatShell): open the onboarding chat once
  // bootstrap is done.
  useEffect(() => {
    if (!chatReady || !onboardingChatRequest) return;
    let cancelled = false;
    (async () => {
      try {
        const ensured = await onboardingAPI.ensureSession();
        if (cancelled || !ensured?.session_id) return;
        setSessionId(ensured.session_id);
        await loadMessagesForSession(ensured.session_id);
      } catch (_) {
        if (!cancelled) setError('Could not open your setup chat.');
      }
    })();
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [chatReady, onboardingChatRequest]);

  // Leaving Chat for Apps/Cases: drop an unused empty draft so it never
  // accumulates in Recent. Outside ChatShell, isChatTabActive is undefined.
  useEffect(() => {
    if (isChatTabActive !== false) return;
    if (!chatReady) return;
    const id = disposableDraftIdRef.current;
    if (!id) return;
    discardEmptyDraftRef.current?.(id, { clearIfActive: true });
  }, [isChatTabActive, chatReady]);

  // Delete leftover default-titled drafts that are not the open chat (e.g. the
  // "New chat" row under a real conversation in Recent).
  const cleanedOrphanIdsRef = useRef(new Set());
  useEffect(() => {
    if (!chatReady) return;
    const ids = orphanEmptyDraftIds(
      [...sessions, ...pinnedSessions],
      sessionId,
      [disposableDraftIdRef.current]
    ).filter((id) => !cleanedOrphanIdsRef.current.has(id));
    if (ids.length === 0) return;
    ids.forEach((id) => cleanedOrphanIdsRef.current.add(id));
    let cancelled = false;
    (async () => {
      await Promise.all(ids.map((id) => chatAPI.deleteSession(id).catch(() => {})));
      if (!cancelled) await refreshSidebar();
    })();
    return () => { cancelled = true; };
  }, [chatReady, sessionId, sessions, pinnedSessions, refreshSidebar]);

  const openCreateProjectModal = (moveSessionId = null) => {
    setPendingMoveSessionId(moveSessionId);
    setNewProjectName('');
    setCreateProjectOpen(true);
  };

  const closeCreateProjectModal = () => {
    if (creatingProject) return;
    setCreateProjectOpen(false);
    setNewProjectName('');
    setPendingMoveSessionId(null);
  };

  const submitCreateProject = async () => {
    const name = (newProjectName || '').trim();
    if (!name || creatingProject) return;
    setCreatingProject(true);
    try {
      const created = await chatAPI.createProject(name);
      if (created?.id) {
        setProjects((prev) => [created, ...prev]);
        setExpandedProjects((prev) => ({ ...prev, [created.id]: true }));
        if (pendingMoveSessionId) {
          await chatAPI.updateChatSession(pendingMoveSessionId, { project_id: created.id });
          await refreshSidebar();
        }
        setCreateProjectOpen(false);
        setNewProjectName('');
        setPendingMoveSessionId(null);
        setActiveProjectViewId(created.id);
      }
    } catch (_) {
      setError('Could not create project.');
    } finally {
      setCreatingProject(false);
    }
  };

  const handleCreateProjectFromMenu = (session) => {
    openCreateProjectModal(session?.id || null);
  };

  const handleDeleteSession = async (id) => {
    if (loading) return;
    try {
      await chatAPI.deleteSession(id);
      const listSafe = await refreshSidebar();
      if (sessionId === id) {
        if (listSafe.length > 0) {
          await handleSelectSession(listSafe[0].id);
        } else {
          // List is now empty — same mandatory-onboarding rule as the
          // mount effect: don't just blank the pane if onboarding is still
          // incomplete, or deleting your last chat becomes another way to
          // skip it. Treat a failed completion check as "not confirmed
          // complete" here too, retrying once before falling back to this
          // function's own existing error state.
          let onboardingIncomplete = false;
          try {
            const stateRes = await onboardingAPI.getState();
            onboardingIncomplete = !stateRes.onboarding_completed_at;
          } catch (_) {
            try {
              const retryState = await onboardingAPI.getState();
              onboardingIncomplete = !retryState.onboarding_completed_at;
            } catch (__) {
              setError('Could not delete chat.');
              return;
            }
          }

          if (onboardingIncomplete) {
            const ensured = await onboardingAPI.ensureSession();
            setSessionId(ensured?.session_id || null);
            if (ensured?.session_id) {
              await loadMessagesForSession(ensured.session_id);
            } else {
              setMessages([]);
            }
            await refreshSidebar();
          } else {
            setSessionId(null);
            setMessages([]);
          }
        }
      }
    } catch (_) {
      setError('Could not delete chat.');
    }
  };

  // Single toggle handler: SessionContextMenu already knows a session's
  // pinned state (pinned_at / session_type) to decide whether to show
  // "Pin" or "Unpin" and which endpoint to call — mirrors how onDelete is
  // one handler regardless of the session being deleted.
  const handleTogglePin = async (session) => {
    if (loading) return;
    try {
      if (session.pinned_at) {
        await chatAPI.unpinSession(session.id);
      } else {
        await chatAPI.pinSession(session.id);
      }
      await refreshSidebar();
    } catch (_) {
      setError(session.pinned_at ? 'Could not unpin chat.' : 'Could not pin chat.');
    }
  };

  const handleStartRename = (session) => {
    setRenamingProjectId(null);
    setRenameProjectDraft('');
    setRenamingSessionId(session.id);
    setRenameDraft(session.title || 'New chat');
  };

  const handleCommitRename = async () => {
    const id = renamingSessionId;
    const title = (renameDraft || '').trim();
    setRenamingSessionId(null);
    setRenameDraft('');
    if (!id || !title) return;
    try {
      const updated = await chatAPI.updateChatSession(id, { title });
      setSessions((prev) => prev.map((s) => (s.id === id ? { ...s, title: updated?.title || title } : s)));
    } catch (_) {
      setError('Could not rename chat.');
    }
  };

  const handleNewProject = () => {
    openCreateProjectModal();
  };

  const handleMoveSessionToProject = async (session, projectId) => {
    try {
      await chatAPI.updateChatSession(session.id, { project_id: projectId });
      await refreshSidebar();
      if (projectId) setExpandedProjects((prev) => ({ ...prev, [projectId]: true }));
    } catch (_) {
      setError('Could not move chat.');
    }
  };

  const handleStartRenameProject = (project) => {
    setRenamingSessionId(null);
    setRenameDraft('');
    setRenamingProjectId(project.id);
    setRenameProjectDraft(project.name || '');
  };

  const handleCommitRenameProject = async () => {
    const id = renamingProjectId;
    const name = (renameProjectDraft || '').trim();
    setRenamingProjectId(null);
    setRenameProjectDraft('');
    if (!id || !name) return;
    try {
      const updated = await chatAPI.updateProject(id, name);
      setProjects((prev) => prev.map((p) => (p.id === id ? { ...p, name: updated?.name || name } : p)));
    } catch (_) {
      setError('Could not rename project.');
    }
  };

  const handleMoveToProject = (session, projectId) => {
    handleMoveSessionToProject(session, projectId);
  };

  const toggleDeepThink = () => {
    setReasoningMode((prev) => {
      const next = !prev;
      localStorage.setItem('chat-reasoning-mode', String(next));
      return next;
    });
  };

  const sessionRowProps = {
    renamingSessionId,
    renameDraft,
    loading,
    projects,
    menuOpen: false,
    moveMenuOpen: false,
    onRenameDraftChange: setRenameDraft,
    onCommitRename: handleCommitRename,
    onCancelRename: () => { setRenamingSessionId(null); setRenameDraft(''); },
    onStartRename: handleStartRename,
    onMoveToProject: handleMoveToProject,
    onCreateProjectFromMenu: handleCreateProjectFromMenu,
    onDelete: handleDeleteSession,
    onTogglePin: handleTogglePin,
    onSelect: handleSelectSession,
    onToggleMenu: (id) => {
      setOpenSessionMenuId((prev) => (prev === id ? null : id));
      setMoveSubmenuOpen(false);
    },
    onToggleMoveMenu: () => setMoveSubmenuOpen((o) => !o),
    onCloseMenu: () => { setOpenSessionMenuId(null); setMoveSubmenuOpen(false); },
  };

  const getSessionRowProps = (sessionId) => ({
    ...sessionRowProps,
    menuOpen: openSessionMenuId === sessionId,
    moveMenuOpen: openSessionMenuId === sessionId && moveSubmenuOpen,
  });

  // Ordinary empty chats, and an empty planning thread, centre the greeting
  // with the composer. A Paths thread that already has messages must not
  // get this: it used to, from the ordinary chat being empty, and the
  // sidebar shift slid that composer under the sidebar.
  const ordinaryEmptyLayout = fullPage && !paths.active && !hasUserMessages && !inProjectView && !isOnboardingSession;
  const planningEmptyLayout = fullPage && paths.showPlanningEmpty;

  return (
    <div
      className={`chat-agent-panel ${fullPage ? 'chat-agent-panel-fullpage' : ''}${fullPage && !sidebarOpen ? ' chat-agent-sidebar-hidden' : ''}${ordinaryEmptyLayout || planningEmptyLayout ? ' chat-agent-empty-layout' : ''}`}
      ref={panelRef}
      aria-label="Chat agents"
    >
      {fullPage && sidebarOpen && (
        <div
          className="chat-agent-sidebar-scrim"
          onClick={() => setSidebarOpenExplicit(false)}
          role="presentation"
        />
      )}
      {fullPage && sidebarOpen && (
        <aside className="chat-agent-sidebar">
          <div style={{ padding: "20px 18px 14px", borderBottom: "1px solid var(--iv-line)", boxSizing: "border-box" }}>
            <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
              <span style={{ fontFamily: "'JetBrains Mono', monospace", fontWeight: 700, fontSize: 13, letterSpacing: "0.12em", color: "var(--iv-head)" }}>
                {brandEduLabel || (
                  <>
                    IVORI <span style={{ opacity: 0.4, fontWeight: 400 }}>EDU</span>
                  </>
                )}
              </span>
              <button
                type="button"
                onClick={() => setSidebarOpenExplicit(false)}
                style={{ background: "transparent", border: "none", cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center", padding: 4, borderRadius: 8 }}
                title="Collapse sidebar"
              >
                <svg width="17" height="17" viewBox="0 0 24 24" fill="none" stroke="var(--iv-muted)" strokeWidth="2"><rect x="3" y="3" width="18" height="18" rx="2" ry="2"/><line x1="9" y1="3" x2="9" y2="21"/></svg>
              </button>
            </div>
          </div>
          <div className="chat-agent-sidebar-search-block">
            <div className="chat-agent-sidebar-search-wrap">
              <svg className="chat-agent-sidebar-search-icon" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <circle cx="11" cy="11" r="8" />
                <path d="M21 21l-4.35-4.35" />
              </svg>
              <input
                type="text"
                className="chat-agent-sidebar-search"
                placeholder="Search chats"
                value={searchChats}
                onChange={(e) => setSearchChats(e.target.value)}
                aria-label="Search chats"
              />
            </div>
          </div>
          <div className="chat-agent-sidebar-new-block">
            <button
              type="button"
              className="chat-agent-sidebar-new"
              onClick={() => handleNewSession()}
              disabled={loading}
              title="Create New Chat"
            >
              <span className="chat-agent-sidebar-new-plus">+</span>
              New chat
            </button>
          </div>
          <div className="chat-agent-sidebar-list">
            {pinnedSessions.length > 0 && (
              <div className="chat-agent-sidebar-group">
                <div className="chat-agent-sidebar-group-label">Pinned</div>
                {filterBySearch(pinnedSessions).map((s) => (
                  <SessionRow key={s.id} session={s} active={sessionId === s.id} {...getSessionRowProps(s.id)} />
                ))}
              </div>
            )}
            <div className="chat-agent-sidebar-section">
              <div className="chat-agent-sidebar-section-header">
                <span className="chat-agent-sidebar-group-label">Projects</span>
                <button type="button" className="chat-agent-sidebar-section-action" onClick={handleNewProject}>
                  + New project
                </button>
              </div>
              {/* The My Path system folder (kind='paths', W2.6) sorts first and
                  is rendered read-only (no rename trigger). GET /api/chat/projects
                  ensures the row (get_or_create_paths_project) so a user who
                  has never started a plan still has the folder and its
                  New path button. Rename and delete are refused server-side
                  (PathsSystemFolderProtectedError). */}
              {[...projects].sort((a, b) => {
                const aPaths = a.kind === 'paths' ? 0 : 1;
                const bPaths = b.kind === 'paths' ? 0 : 1;
                return aPaths - bPaths;
              }).map((project) => {
                const isPathsFolder = project.kind === 'paths';
                // The My Path folder lists paths, not chat sessions: see
                // features/paths/components/MyPathFolder.js.
                if (isPathsFolder && pathsEnabled && fullPage) {
                  return (
                    <MyPathFolder
                      key={project.id}
                      name={project.name}
                      controller={paths}
                      refreshKey={paths.pathsChangedKey}
                      filterText={searchChats}
                      onNavigate={() => {
                        setActiveProjectViewId(null);
                        if (window.innerWidth <= 768) setSidebarOpenExplicit(false);
                      }}
                    />
                  );
                }
                const projectSessions = filterBySearch(sessions.filter((s) => s.project_id === project.id));
                const expanded = expandedProjects[project.id] === true;
                const isActiveProject = activeProjectViewId === project.id;
                return (
                  <div
                    key={project.id}
                    className={`chat-agent-sidebar-project${isActiveProject ? ' active' : ''}${isPathsFolder ? ' chat-agent-sidebar-project-paths' : ''}`}
                  >
                    {renamingProjectId === project.id ? (
                      <input
                        type="text"
                        className="chat-agent-sidebar-rename-input chat-agent-sidebar-project-rename-input"
                        value={renameProjectDraft}
                        onChange={(e) => setRenameProjectDraft(e.target.value)}
                        onBlur={handleCommitRenameProject}
                        onKeyDown={(e) => {
                          if (e.key === 'Enter') { e.preventDefault(); handleCommitRenameProject(); }
                          if (e.key === 'Escape') {
                            e.preventDefault();
                            setRenamingProjectId(null);
                            setRenameProjectDraft('');
                          }
                        }}
                        autoFocus
                        aria-label="Rename project"
                      />
                    ) : (
                      <div className={`chat-agent-sidebar-project-header glass-pill ${isActiveProject ? 'active' : ''}`}>
                        <button
                          type="button"
                          className="chat-agent-sidebar-project-toggle"
                          onClick={() => setExpandedProjects((prev) => ({ ...prev, [project.id]: !expanded }))}
                          aria-expanded={expanded}
                          aria-label={`${expanded ? 'Collapse' : 'Expand'} ${project.name}`}
                        >
                          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" className={expanded ? 'expanded' : ''}>
                            <path d="M9 18l6-6-6-6" />
                          </svg>
                        </button>
                        {isPathsFolder && (
                          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" className="chat-agent-sidebar-project-paths-icon">
                            <path d="M3 12l6-6 4 4 8-8" />
                            <path d="M17 2h4v4" />
                          </svg>
                        )}
                        <button
                          type="button"
                          className="chat-agent-sidebar-project-name"
                          onClick={() => handleOpenProjectView(project.id)}
                          onDoubleClick={isPathsFolder ? undefined : () => handleStartRenameProject(project)}
                          title={isPathsFolder ? 'Open My Path' : 'Open project'}
                        >
                          {project.name}
                        </button>
                      </div>
                    )}
                    {expanded && projectSessions.map((s) => (
                      <SessionRow key={s.id} session={s} active={sessionId === s.id} {...getSessionRowProps(s.id)} />
                    ))}
                  </div>
                );
              })}
            </div>
            {visibleUnassignedSessions.length > 0 && (
              <div className="chat-agent-sidebar-group">
                <div className="chat-agent-sidebar-group-label">Recent</div>
                {visibleUnassignedSessions.map((s) => (
                  <SessionRow key={s.id} session={s} active={sessionId === s.id} {...getSessionRowProps(s.id)} />
                ))}
                {canShowMoreSessions && (
                  <button
                    type="button"
                    className="chat-agent-sidebar-show-more"
                    onClick={handleShowMoreSessions}
                    disabled={loadingMoreSessions}
                  >
                    {loadingMoreSessions ? 'Loading…' : 'Show more'}
                  </button>
                )}
              </div>
            )}
          </div>
        </aside>
      )}
      <div className="chat-agent-main">
        {fullPage && !sidebarOpen && (
          <button
            type="button"
            className="chat-agent-sidebar-expand-float"
            onClick={() => setSidebarOpenExplicit(true)}
            title="Expand sidebar"
          >
            <svg width="17" height="17" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><rect x="3" y="3" width="18" height="18" rx="2" ry="2"/><line x1="9" y1="3" x2="9" y2="21"/></svg>
          </button>
        )}
        {!fullPage && (
          <div className="chat-agent-sessions-row">
            <button type="button" className="chat-agent-session-new" onClick={() => handleNewSession()} disabled={loading}>+ New chat</button>
            <div className="chat-agent-session-list">
              {sessions.filter((s) => !shouldHideSessionFromSidebar(s, {
                activeSessionId: sessionId,
                activeHasUserMessages: hasUserMessages,
              })).map((s) => (
                <button key={s.id} type="button" className={`chat-agent-session-pill ${sessionId === s.id ? 'active' : ''}`} onClick={() => handleSelectSession(s.id)} disabled={loading}>
                  <span className="chat-agent-session-title">{s.title || 'New chat'}</span>
                </button>
              ))}
            </div>
          </div>
        )}

        {inProjectView ? (
          <div className="chat-agent-project-view">
            <div className="chat-agent-project-view-header">
              <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8">
                <path d="M22 19a2 2 0 01-2 2H4a2 2 0 01-2-2V5a2 2 0 012-2h5l2 3h9a2 2 0 012 2z" />
              </svg>
              <h1 className="chat-agent-project-view-title">{activeProject.name}</h1>
            </div>
            <button
              type="button"
              className="chat-agent-project-new-chat"
              onClick={() => handleNewSession(activeProject.id)}
              disabled={loading}
            >
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <path d="M12 5v14M5 12h14" />
              </svg>
              New chat in {activeProject.name}
            </button>
            <div className="chat-agent-project-view-tabs">
              <span className="chat-agent-project-view-tab active">Chats</span>
            </div>
            <div className="chat-agent-project-chat-list">
              {activeProjectSessions.length === 0 ? (
                <p className="chat-agent-project-empty">No chats in this project yet. Start one above.</p>
              ) : (
                activeProjectSessions.map((s) => (
                  <button
                    key={s.id}
                    type="button"
                    className={`chat-agent-project-chat-card${sessionId === s.id ? ' active' : ''}`}
                    onClick={() => handleSelectSession(s.id)}
                    disabled={loading}
                  >
                    <div className="chat-agent-project-chat-card-top">
                      <span className="chat-agent-project-chat-card-title">{s.title || 'New chat'}</span>
                      <span className="chat-agent-project-chat-card-date">
                        {formatSessionDate(s.updated_at || s.created_at)}
                      </span>
                    </div>
                    {projectPreviews[s.id] && (
                      <p className="chat-agent-project-chat-card-preview">{projectPreviews[s.id]}</p>
                    )}
                  </button>
                ))
              )}
            </div>
          </div>
        ) : (
        <PathsChatSurface controller={paths}>
        {paths.threadActive ? <PathsThread controller={paths} /> : (
        <div className={`chat-agent-messages ${fullPage ? 'chat-agent-messages-fullpage' : ''}`}>
          <div className="chat-agent-messages-inner">
            {!hasUserMessages && !loading && fullPage && !(isOnboardingSession && (onboardingNextStep || onboardingFullyComplete)) && (
              <div className="chat-agent-wow-empty-state">
                {/* Dynamic Greeting */}
                <h1 className="chat-wow-greeting">
                  {(() => {
                    const hour = new Date().getHours();
                    if (hour < 12) return 'Good morning';
                    if (hour < 18) return 'Good afternoon';
                    return 'Good evening';
                  })()}{isInstructor && onboardingKnownComplete
                    ? ((user?.first_name || user?.name) ? `, ${user.first_name || user.name}` : '')
                    : `, ${user?.first_name || user?.name || 'Student'}`}
                </h1>
                {isInstructor && onboardingKnownComplete && (
                  <div className="chat-agent-suggestions" data-testid="instructor-starter-prompts">
                    {INSTRUCTOR_STARTER_PROMPTS.map((prompt) => (
                      <button
                        key={prompt}
                        type="button"
                        className="chat-agent-suggestion-chip"
                        onClick={() => {
                          setInput(prompt);
                          textareaRef.current?.focus();
                        }}
                      >
                        {prompt}
                      </button>
                    ))}
                  </div>
                )}
              </div>
            )}
            {!hasUserMessages && !loading && !fullPage && (
              <p className="chat-agent-empty">Send a message to start.</p>
            )}
            {messages.map((m) => {
              if (onboardingDividerIds.has(String(m.id))) {
                return <OnboardingDivider key={m.id} text={m.message} />;
              }
              const isUser = m.sender === 'user';
              return (
                <div
                  key={m.id}
                  className={`chat-agent-msg-row ${discussionMode ? 'discussion' : (isUser ? 'user' : 'bot')}${!isUser && (m.id === animatingBotMessageId || m.id === streamingBotMessageId) ? ' typing' : ''}`}
                >
                  {!isUser && !discussionMode && (
                    <div className="chat-agent-bot-avatar-badge" title="uAI Assistant">
                      <img src="/logo-yellow.png" alt="uAI Logo" style={{ width: '20px', height: '20px', objectFit: 'contain' }} />
                    </div>
                  )}
                  <div className={`chat-agent-msg ${discussionMode ? 'discussion' : (isUser ? 'user' : 'bot')}`}>
                    {discussionMode && (
                      <div className="chat-agent-msg-header">
                        <div className={`chat-agent-msg-avatar ${isUser ? 'user' : 'bot'}`}>
                          {isUser ? (
                              user?.first_name ? user.first_name[0].toUpperCase() : (user?.name ? user.name[0].toUpperCase() : 'U')
                          ) : 'AI'}
                        </div>
                        <span className="chat-agent-msg-author">{isUser ? 'You' : 'uAI Assistant'}</span>
                        <span className="chat-agent-msg-time">
                          {m.created_at ? new Date(m.created_at).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }) : ''}
                        </span>
                      </div>
                    )}
                    {m.sender === 'bot' ? (
                      <BotMessageContent
                        text={m.message}
                        animate={m.id === animatingBotMessageId}
                        partial={m.id === streamingBotMessageId}
                        showPauseIndicator={m.id === streamingBotMessageId && streamPaused}
                        onComplete={handleTypewriterComplete}
                        onProgress={handleTypewriterProgress}
                      />
                    ) : (
                      <div className="chat-agent-msg-content">{isUser ? renderUserMessage(m.message) : m.message}</div>
                    )}
                  </div>
                </div>
              );
            })}
            {isOnboardingSession && (onboardingFullyComplete || (onboardingNextStep && onboardingNextStep.kind === 'summary')) && (
              <OnboardingSummaryCard
                answers={onboardingRoundAnswers}
                stepsById={Object.fromEntries((onboardingSteps || []).map((s) => [s.id, s]))}
                signupName={{ first: user?.first_name, last: user?.last_name }}
                onFinish={handleOnboardingFinish}
                onSaveEdit={handleOnboardingEditSave}
                completed={onboardingFullyComplete}
              />
            )}
            {isOnboardingSession && onboardingInvitation && !onboardingRedo && (
              <OnboardingInvitationCard
                text={onboardingInvitation.text}
                onAccept={handleInvitationAccept}
                onDismiss={handleInvitationDismiss}
              />
            )}
            {onboardingNextStep && onboardingNextStep.kind !== 'summary' && (
              onboardingSaving ? (
                // Phase 2: the pending step's question is now a real,
                // already-stored chat_messages row (written by the
                // *previous* save_answer call, or by session creation for
                // the first step) — no virtual/live-rendered prompt row is
                // needed here any more, unlike Phase 1. What replaces it:
                // submitting an answer can trigger an up-to-3-second model
                // call to phrase the *next* question, and until that
                // reload lands there's nothing new on screen — this
                // typing indicator fills that gap so a slow call doesn't
                // read as a broken button. Same three-dot pulse treatment
                // as the landing page's typing indicator
                // (EduLandingChatPrompt.js), adapted to this panel's own
                // message-row/avatar styling.
                <div className="chat-agent-msg-row bot">
                  <div className="chat-agent-bot-avatar-badge" title="uAI Assistant">
                    <img src="/logo-yellow.png" alt="uAI Logo" style={{ width: '20px', height: '20px', objectFit: 'contain' }} />
                  </div>
                  <div className="chat-agent-msg bot" aria-label="uAI Assistant is typing">
                    <div className="chat-agent-typing-dots">
                      <span className="chat-agent-typing-dot" />
                      <span className="chat-agent-typing-dot" />
                      <span className="chat-agent-typing-dot" />
                    </div>
                  </div>
                </div>
              ) : (
                <OnboardingOptionCard
                  key={onboardingNextStep.id}
                  step={onboardingNextStep}
                  stepIndex={onboardingStepIndex}
                  totalSteps={onboardingActiveSteps.filter((s) => s.kind !== 'summary').length}
                  onAnswer={handleOnboardingAnswer}
                />
              )
            )}
            {awaitingBotReply && !streamingBotMessageId && (
              <div className={`chat-agent-msg-row bot thinking ${discussionMode ? 'discussion' : ''}`}>
                {!discussionMode && (
                  <div className="chat-agent-bot-avatar-badge" title="uAI Assistant">
                    <img src="/logo-yellow.png" alt="uAI Logo" style={{ width: '20px', height: '20px', objectFit: 'contain' }} />
                  </div>
                )}
                <div className={`chat-agent-msg bot ${discussionMode ? 'discussion' : ''}`}>
                  {discussionMode && (
                    <div className="chat-agent-msg-header">
                      <div className="chat-agent-msg-avatar bot">AI</div>
                      <span className="chat-agent-msg-author">uAI Assistant</span>
                    </div>
                  )}
                  <ThinkingIndicator />
                </div>
              </div>
            )}
            <div ref={messagesEndRef} />
          </div>
        </div>
        )}

        <div className="chat-agent-input-wrap">

          {attachedFiles.length > 0 && (
            <div className="chat-agent-previews">
              {attachedFiles.map((file, idx) => (
                <div key={idx} className="chat-agent-preview-chip">
                  {file.type === 'image' ? (
                    <img src={file.base64} alt={file.name} className="chat-agent-preview-image" />
                  ) : (
                    <div className="chat-agent-preview-icon">
                      <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                        <path d="M14 2H6a2 2 0 00-2 2v16a2 2 0 002 2h12a2 2 0 002-2V8z" />
                        <polyline points="14 2 14 8 20 8" />
                      </svg>
                    </div>
                  )}
                  <div className="chat-agent-preview-details">
                    <span className="chat-agent-preview-name">{file.name}</span>
                    <span className="chat-agent-preview-size">{(file.size / 1024).toFixed(1)} KB</span>
                  </div>
                  <button
                    type="button"
                    className="chat-agent-preview-remove"
                    onClick={() => removeAttachedFile(idx)}
                    aria-label="Remove file"
                  >
                    <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
                      <line x1="18" y1="6" x2="6" y2="18" />
                      <line x1="6" y1="6" x2="18" y2="18" />
                    </svg>
                  </button>
                </div>
              ))}
            </div>
          )}

          {fileUploading && (
            <div className="chat-agent-file-uploading-indicator">
              <span className="chat-agent-upload-spinner" />
              <span>Uploading and parsing file...</span>
            </div>
          )}

          <input
            type="file"
            ref={fileInputRef}
            style={{ display: 'none' }}
            multiple
            accept="image/*,.pdf,.docx,.txt,.csv,.json,.py,.js"
            onChange={handleFileChange}
          />

          <PathsComposerAccessory controller={paths} />
          <div className="chat-agent-prompt-card" ref={promptCardRef}>
            {/* 1. Auto-growing Textarea row — replaced entirely once this
                onboarding session is permanently done, since a disabled
                textarea reading "Answer above to continue" has nothing left
                above it to point at once every step is answered. This
                session never becomes an ordinary chat again; the pointer
                always leads to a new one. */}
            {onboardingFullyComplete ? (
              <div className="chat-agent-prompt-textarea-row chat-agent-prompt-done">
                <span>
                  This chat is for setting up your profile.{' '}
                  <button type="button" className="chat-agent-prompt-done-link" onClick={() => handleNewSession()}>
                    Start a new chat
                  </button>
                  {' '}to talk with {brandName}.
                </span>
              </div>
            ) : (
              <div className={`chat-agent-prompt-textarea-row${composerLocked ? ' chat-agent-prompt-locked' : ''}`}>
                <textarea
                  ref={textareaRef}
                  className="chat-agent-prompt-textarea"
                  placeholder={
                    composerLocked
                      ? (composerLockReason || 'Answer above to continue')
                      : (paths.active && paths.generating
                        ? (paths.lockReason || 'Wait for the draft to finish')
                        : (paths.active && paths.placeholder
                          ? paths.placeholder
                          : (fullPage ? 'How can I help you today?' : 'Assign a task or ask anything...')))
                  }
                  value={input}
                  onChange={(e) => setInput(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' && !e.shiftKey) {
                      e.preventDefault();
                      handleSend();
                    }
                  }}
                  disabled={!(sessionId || paths.active) || composerLocked || (paths.active && paths.generating)}
                  rows={1}
                  aria-label="Message"
                />
              </div>
            )}

            {/* 2. Embedded Tool Selectors and Send Button Footer Row */}
            <div className="chat-agent-prompt-footer-row">

              {/* Left group: attach a file directly. Used to open a popup
                  menu with Attach plus Planner/Auto/Usage limits; those
                  three moved out to sit next to Deep Think below, so this
                  button now performs its one remaining action immediately. */}
              <div className="chat-agent-prompt-footer-left">
                <div className="chat-agent-plus-container">
                  <button
                    type="button"
                    className="chat-agent-plus-btn"
                    onClick={() => fileInputRef.current?.click()}
                    title={composerLocked ? 'Answer above to continue' : 'Attach a file'}
                    disabled={!sessionId || paths.active || fileUploading || composerLocked}
                  >
                    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
                      <path d="M21.44 11.05l-9.19 9.19a6 6 0 01-8.49-8.49l9.19-9.19a4 4 0 015.66 5.66l-9.2 9.19a2 2 0 01-2.83-2.83l8.49-8.48" />
                    </svg>
                  </button>
                </div>
              </div>

              {/* Right group: Deep Think and send. Planner/Auto pickers are
                  hidden in the composer for now; defaults still go to the
                  backend so those modes can be restored later. */}
              {!onboardingFullyComplete && (
                <div className="chat-agent-prompt-footer-right">
                  <button
                    type="button"
                    className={`chat-agent-deep-think-toggle${reasoningMode ? ' on' : ''}`}
                    onClick={toggleDeepThink}
                    disabled={!sessionId || paths.active}
                    title="Deep Think: for questions worth more time"
                  >
                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
                      <path d="M12 3l2.2 5.3L20 10l-5.8 1.7L12 17l-2.2-5.3L4 10l5.8-1.7z" />
                    </svg>
                    <span>Deep Think</span>
                  </button>
                  <ChatSendSlot
                    input={input}
                    loading={loading}
                    sessionId={paths.active ? paths.planSessionId : sessionId}
                    locked={composerLocked || (paths.active && !paths.composerReady)}
                    onSend={() => handleSend()}
                  />
                </div>
              )}

            </div>
          </div>
          {error && <p className="chat-agent-error">{error}</p>}
        </div>
        </PathsChatSurface>
        )}
      </div>
      <CreateProjectModal
        open={createProjectOpen}
        name={newProjectName}
        onNameChange={setNewProjectName}
        onClose={closeCreateProjectModal}
        onCreate={submitCreateProject}
        creating={creatingProject}
      />
    </div>
  );
}

export default ChatAgentPanel;
